/**
 * eReg Dashboard Page
 *
 * Overview of pending signatures, missing documents, and protocol status.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PenTool,
  AlertTriangle,
  Clock,
  FileText,
  ChevronRight,
  FolderOpen,
  Inbox,
  Plus,
  Star,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, SearchInput, chipCls } from '../../ui';
import * as eregApi from '../lib/api';
import { getStudyName, compareStudiesByName } from '../types';
import type { DashboardStatsResponse, Protocol, SearchResult } from '../types';

export function Dashboard() {
  const { protocols, protocolsLoading, setCurrentProtocol, setSiteMode, isAdmin } = useEreg();
  const { currentSite } = useAuth();
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  // "My Studies" follows the selected site like the stat cards do (N-3: the
  // numbers and the list used to disagree); "All sites" opts back out.
  const [showAllSites, setShowAllSites] = useState(false);

  const hasOtherSiteStudies = !!currentSite && protocols.some((p) => p.site_id !== currentSite.id);
  const visibleProtocols =
    !currentSite || showAllSites ? protocols : protocols.filter((p) => p.site_id === currentSite.id);

  // Starring is a personal "My Studies" sort preference (persisted server-side
  // per user). Overrides give the toggle an instant response instead of
  // waiting on a refetch; keyed by protocol id, `null` = explicitly unstarred.
  const [starOverrides, setStarOverrides] = useState<Record<string, string | null>>({});

  const sortedProtocols = useMemo(() => {
    const withStar = visibleProtocols.map((protocol) => {
      const override = starOverrides[protocol.id];
      const starredAt = override !== undefined ? override : protocol.starred_at ?? null;
      return { protocol, starredAt };
    });
    const starred = withStar
      .filter((entry) => entry.starredAt)
      .sort((a, b) => compareStudiesByName(a.protocol, b.protocol));
    const rest = withStar
      .filter((entry) => !entry.starredAt)
      .sort((a, b) => compareStudiesByName(a.protocol, b.protocol));
    return [...starred, ...rest].map((entry) => entry.protocol);
  }, [visibleProtocols, starOverrides]);

  async function handleToggleStar(e: React.MouseEvent, protocol: Protocol) {
    e.preventDefault();
    e.stopPropagation();
    const override = starOverrides[protocol.id];
    const currentlyStarred = !!(override !== undefined ? override : protocol.starred_at);
    setStarOverrides((prev) => ({ ...prev, [protocol.id]: currentlyStarred ? null : new Date().toISOString() }));
    try {
      if (currentlyStarred) {
        await eregApi.unstarProtocol(protocol.id);
      } else {
        const result = await eregApi.starProtocol(protocol.id);
        setStarOverrides((prev) => ({ ...prev, [protocol.id]: result.starred_at }));
      }
    } catch (error) {
      console.error('[eReg] Failed to toggle study star:', error);
      // Revert to server truth rather than guessing — drop the optimistic override.
      setStarOverrides((prev) => {
        const next = { ...prev };
        delete next[protocol.id];
        return next;
      });
    }
  }

  // Fetch dashboard stats
  useEffect(() => {
    async function fetchStats() {
      setStatsLoading(true);
      setStatsError(false);
      try {
        const dashboardStats = await eregApi.getDashboardStats({
          site_id: currentSite?.id,
        });
        setStats(dashboardStats);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        // A failed fetch must not render as reassuring zeros.
        setStats(null);
        setStatsError(true);
      } finally {
        setStatsLoading(false);
      }
    }

    if (currentSite?.id) {
      fetchStats();
    }
  }, [currentSite?.id]);

  // '-' while loading, em-dash on error — never a false 0.
  const statValue = (value: number | undefined) =>
    statsLoading ? '-' : statsError ? '—' : value ?? 0;

  const handleProtocolClick = (protocol: typeof protocols[0]) => {
    setCurrentProtocol(protocol);
    setSiteMode(false);
  };

  const handleSiteDocsClick = () => {
    setCurrentProtocol(null);
    setSiteMode(true);
  };

  // Handle search
  async function handleSearch() {
    if (searchQuery.length < 2) return;

    setIsSearching(true);
    try {
      const response = await eregApi.searchDocuments({
        query: searchQuery,
        site_id: currentSite?.id,
        limit: 10,
      });
      setSearchResults(response.results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }

  // Debounce search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentSite?.id]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header with Search */}
      <PageHeader
        title="eReg Dashboard"
        subtitle="Electronic Regulatory Document Management"
        actions={
          <div className="relative w-full md:w-80">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search documents..."
              label="Search documents"
            />

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 max-h-80 overflow-auto">
                {searchResults.map((result) => (
                  <Link
                    key={result.id}
                    // Study docs route through the /ereg/studies/:id hydrator
                    // (selects the study + enrolls); site docs flip siteMode.
                    // ?folder_id= is honored by Folders once the tree loads.
                    to={
                      result.protocol?.id
                        ? `/ereg/studies/${result.protocol.id}/folders${result.folder?.id ? `?folder_id=${result.folder.id}` : ''}`
                        : `/ereg/site/folders${result.folder?.id ? `?folder_id=${result.folder.id}` : ''}`
                    }
                    onClick={() => {
                      if (!result.protocol?.id) {
                        setSiteMode(true);
                      }
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="flex items-center gap-3 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {result.document_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {result.folder?.path} • {result.document_type}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        }
      />

      {/* Stats Cards */}
      {statsError && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
          <AlertTriangle size={16} />
          Couldn't load dashboard counts — the numbers below are unavailable, not zero.
        </div>
      )}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        {/* Pending Signatures */}
        <Link
          to="/ereg/signatures"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-md transition-all duration-150 dark:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Signatures</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {statValue(stats?.pending_signatures)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <PenTool className="text-purple-600 dark:text-purple-400" size={24} />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm text-purple-600 dark:text-purple-400 font-medium">
            Sign Now <ChevronRight size={16} />
          </div>
        </Link>

        {/* Missing Documents */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Missing Documents</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {statValue(stats?.missing_documents)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="text-amber-600 dark:text-amber-400" size={24} />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Unfulfilled placeholders
          </div>
        </div>

        {/* Expiring Documents */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiring Soon</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {statValue(stats?.expiring_documents)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
              <Clock className="text-red-600 dark:text-red-400" size={24} />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Within 30 days
          </div>
        </div>

        {/* Intake inbox (P-18) — bulk-dropped documents were invisible outside
            the admin-only intake page; the tile matches that gating. */}
        {isAdmin && (
          <Link
            to="/ereg/intake"
            className="bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-md transition-all duration-150 dark:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-600"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Intake Inbox</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {statValue(stats?.pending_intake)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <Inbox className="text-primary-600 dark:text-primary-400" size={24} />
              </div>
            </div>
            <div className="mt-3 flex items-center text-sm text-primary-600 dark:text-primary-400 font-medium">
              File Documents <ChevronRight size={16} />
            </div>
          </Link>
        )}

      </div>

      {/* Site Documents Quick Access */}
      {currentSite && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FolderOpen size={20} className="text-primary-600 dark:text-primary-400" />
              Site Documents
            </h2>
            <Link
              to="/ereg/site/folders"
              onClick={handleSiteDocsClick}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium flex items-center gap-1"
            >
              Open <ChevronRight size={16} />
            </Link>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage CVs, licenses, training certificates, and other site-level documents
            that can be linked to multiple studies.
          </p>
        </div>
      )}

      {/* Protocols List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText size={20} className="text-primary-600 dark:text-primary-400" />
            My Studies
          </h2>
          {hasOtherSiteStudies && currentSite && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowAllSites(false)}
                className={chipCls(!showAllSites)}
                aria-pressed={!showAllSites}
              >
                {currentSite.name}
              </button>
              <button
                type="button"
                onClick={() => setShowAllSites(true)}
                className={chipCls(showAllSites)}
                aria-pressed={showAllSites}
              >
                All sites
              </button>
            </div>
          )}
        </div>

        {protocolsLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading studies...</p>
          </div>
        ) : protocols.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No studies available</p>
            <p className="text-sm mt-1 mb-4">Create a new study to get started</p>
            <Link
              to="/ereg/studies/new"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <Plus size={16} />
              New Study
            </Link>
          </div>
        ) : visibleProtocols.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No studies at {currentSite?.name}</p>
            <button
              type="button"
              onClick={() => setShowAllSites(true)}
              className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Show all sites
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedProtocols.map((protocol) => {
              const protocolStat = stats?.protocols?.find(p => p.id === protocol.id);
              const override = starOverrides[protocol.id];
              const starredAt = override !== undefined ? override : protocol.starred_at ?? null;
              const starred = !!starredAt;
              const metaLine = [protocol.sponsor_name, protocol.site?.name].filter(Boolean).join(' • ');
              const displayName = getStudyName(protocol);
              const hasNickname = displayName !== protocol.protocol_number;
              return (
                <Link
                  key={protocol.id}
                  to={`/ereg/studies/${protocol.id}/folders`}
                  onClick={() => handleProtocolClick(protocol)}
                  className="group relative flex flex-col rounded-lg border border-gray-200 bg-white p-4 transition-all duration-150 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
                >
                  <button
                    type="button"
                    onClick={(e) => handleToggleStar(e, protocol)}
                    aria-label={starred ? `Unstar ${displayName}` : `Star ${displayName}`}
                    aria-pressed={starred}
                    className="absolute right-1.5 top-1.5 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-300 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-600 dark:hover:bg-gray-700"
                  >
                    <Star
                      size={18}
                      className={starred ? 'fill-primary-600 text-primary-600 dark:fill-primary-400 dark:text-primary-400' : ''}
                    />
                  </button>

                  <div className="flex items-start gap-2.5 pr-8">
                    <div className="flex-shrink-0 rounded-lg bg-primary-50 p-2 dark:bg-primary-900/20">
                      <FileText size={18} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">
                        {displayName}
                      </h3>
                      <span className={`mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        protocol.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : protocol.status === 'startup'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                            : protocol.status === 'closed'
                              ? 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'
                      }`}>
                        {protocol.status}
                      </span>
                    </div>
                  </div>

                  {hasNickname && (
                    <p className="mt-2 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                      {protocol.protocol_number}
                    </p>
                  )}

                  {(protocolStat?.placeholder_total || protocolStat?.expiring_count) ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {protocolStat && protocolStat.placeholder_total > 0 && (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          protocolStat.placeholder_fulfilled === protocolStat.placeholder_total
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}>
                          {protocolStat.placeholder_fulfilled}/{protocolStat.placeholder_total} docs
                        </span>
                      )}
                      {protocolStat && protocolStat.expiring_count > 0 && (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                          {protocolStat.expiring_count} expiring
                        </span>
                      )}
                    </div>
                  ) : null}

                  <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
                    <span className="truncate">{metaLine || ' '}</span>
                    <ChevronRight size={16} className="flex-shrink-0 text-gray-300 transition-colors duration-150 group-hover:text-primary-500 dark:text-gray-600" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
